import { useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { cn } from '../../lib/cn'
import { dayjs, daysBetween } from '../../lib/format'
import { useFormat, useT } from '../../lib/i18n'
import type { SimpleTKey } from '../../lib/i18n'
import { RANGE_PRESETS, resolvePreset } from '../../lib/range'
import type { DateRange, RangePresetId } from '../../lib/range'
import { Button } from './Button'
import { Icon } from './Icon'
import { Popover } from './Popover'

/**
 * `lib/range.ts` owns the arithmetic and carries an English `label` for its own logs; the
 * words on screen come from here. An explicit map rather than a key built from the id, so a
 * new preset is a compile error until it is translated.
 */
const PRESET_KEY: Readonly<Record<Exclude<RangePresetId, 'custom'>, SimpleTKey>> = {
  last7: 'common.time.last7Days',
  last28: 'components.dateRange.last28',
  last30: 'common.time.last30Days',
  last90: 'common.time.last90Days',
  thisMonth: 'common.time.thisMonth',
  lastMonth: 'components.dateRange.lastMonth',
}

export interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  /** `chrome` for the top bar, `paper` for a filter row inside the canvas. */
  tone?: 'paper' | 'chrome'
  className?: string
}

const todayIso = (): string => dayjs().format('YYYY-MM-DD')

/**
 * Range control for every analytics surface. Presets first, because that is what people pick;
 * the two date fields are native so mobile gets a proper calendar for free.
 */
export const DateRangePicker = ({
  value,
  onChange,
  tone = 'paper',
  className,
}: DateRangePickerProps): ReactElement => {
  const t = useT()
  const f = useFormat()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(value.from)
  const [draftTo, setDraftTo] = useState(value.to)

  const openPanel = (): void => {
    setDraftFrom(value.from)
    setDraftTo(value.to)
    setOpen(true)
  }

  const applyCustom = (): void => {
    const ordered = dayjs(draftFrom).isAfter(dayjs(draftTo))
      ? { from: draftTo, to: draftFrom }
      : { from: draftFrom, to: draftTo }
    onChange({ ...ordered, preset: 'custom' })
    setOpen(false)
  }

  // Plain `YYYY-MM-DD` calendar dates: no zone, and the locale decides `11 Jul – 7 Aug 2026`
  // against `11 lug – 7 ago 2026`.
  const label = f.range(value.from, value.to)
  const spanDays = daysBetween(value.from, value.to)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (open) setOpen(false)
          else openPanel()
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'ring-focus t-small flex h-9 items-center gap-2 rounded-sm border px-3 transition-colors',
          tone === 'chrome'
            ? 'border-chrome-600 bg-chrome-800 text-chrome-ink hover:bg-chrome-700'
            : 'border-hair bg-surface text-ink hover:bg-sunken',
          className,
        )}
      >
        <Icon
          name="calendar"
          size={15}
          className={tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint'}
        />
        <span className="whitespace-nowrap">{label}</span>
        <Icon
          name="chevron-down"
          size={14}
          className={tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint'}
        />
      </button>

      <Popover
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        anchorRef={triggerRef}
        placement="bottom"
        align="end"
        label={t('components.dateRange.panelLabel')}
        className="w-[min(22rem,calc(100vw-1rem))] p-3"
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-1">
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => {
                  onChange(resolvePreset(preset.id))
                  setOpen(false)
                }}
                className={cn(
                  't-small ring-focus rounded-sm px-2.5 py-1.5 text-left transition-colors',
                  value.preset === preset.id
                    ? 'bg-brass-wash text-brass-deep'
                    : 'text-ink-mute hover:bg-sunken hover:text-ink',
                )}
              >
                {t(PRESET_KEY[preset.id])}
              </button>
            ))}
          </div>

          <div className="border-t border-hair pt-3">
            <p className="t-label mb-2 text-ink-mute">{t('common.time.custom')}</p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={draftFrom}
                max={todayIso()}
                aria-label={t('components.dateRange.start')}
                onChange={(event) => {
                  setDraftFrom(event.target.value)
                }}
                className="ring-focus t-small h-9 w-full rounded-sm border border-hair bg-surface px-2 text-ink"
              />
              <span className="t-mono text-ink-mute">{t('components.dateRange.to')}</span>
              <input
                type="date"
                value={draftTo}
                max={todayIso()}
                aria-label={t('components.dateRange.end')}
                onChange={(event) => {
                  setDraftTo(event.target.value)
                }}
                className="ring-focus t-small h-9 w-full rounded-sm border border-hair bg-surface px-2 text-ink"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="t-mono text-ink-mute">
              {t('components.dateRange.selectedDays', { count: spanDays })}
            </p>
            <Button variant="primary" size="sm" onClick={applyCustom}>
              {t('components.dateRange.apply')}
            </Button>
          </div>
        </div>
      </Popover>
    </>
  )
}
